import { useState } from "react";
import { distinct } from "@entrilia/shared/utilities/arrayHelper";
import type { ValidationResult } from "@entrilia/shared/utilities/validators";
import { combineValidators, maxCharactersValidator, requiredValidator } from "@entrilia/shared/utilities/validators";
import type { ReportField } from "../../../../../../../../../../api/types/svTypes";
import { getFieldCaption } from "../../../../../../utilities/Utilities";
import type { DateFieldOption } from "../controls/DateToShowSelect";
import type { DateCalculationType } from "../utilities/dateCalculationTypes";
import { buildDateCalculationDefaultName } from "../utilities/dateCalculationTypes";

interface FormState {
  name: string;
  selectedFormulaParameterIds: string[];
  selectedType: DateCalculationType;
  selectedDateField: string | undefined;
  touchedFields: string[];
}

const validateName = combineValidators(requiredValidator, maxCharactersValidator(255));

const useSetupDateCalculationMenu = (
  initialType: DateCalculationType,
  fieldGuid: string | undefined,
  eligibleFields: ReportField[],
  dateFields: DateFieldOption[]
) => {
  const [form, setForm] = useState<FormState>(() => {
    const currentField = eligibleFields.find((f) => f.config.guid === fieldGuid) ?? eligibleFields[0];
    return {
      selectedType: initialType,
      selectedFormulaParameterIds: currentField ? [currentField.config.guid] : [],
      selectedDateField: dateFields[0]?.name,
      name: buildDateCalculationDefaultName(
        initialType,
        dateFields[0]?.caption,
        currentField ? getFieldCaption(currentField) : undefined
      ),
      touchedFields: [],
    };
  });

  const recomputeName = (next: FormState): string => {
    if (next.touchedFields.includes("name")) {
      return next.name;
    }
    const field = eligibleFields.find((f) => f.config.guid === next.selectedFormulaParameterIds[0]);
    const dateField = dateFields.find((d) => d.name === next.selectedDateField);
    return buildDateCalculationDefaultName(
      next.selectedType,
      dateField?.caption,
      field ? getFieldCaption(field) : undefined
    );
  };

  const update = (changes: Partial<FormState>, touched: keyof FormState) => {
    setForm((prev) => {
      const next: FormState = { ...prev, ...changes, touchedFields: distinct([...prev.touchedFields, touched]) };
      return { ...next, name: recomputeName(next) };
    });
  };

  const nameResult: ValidationResult = validateName(form.name);
  const isValid = nameResult.isValid && !!form.selectedFormulaParameterIds[0] && !!form.selectedDateField;

  return {
    name: form.name,
    selectedType: form.selectedType,
    selectedFormulaParameterIds: form.selectedFormulaParameterIds,
    selectedDateField: form.selectedDateField,
    isValid,
    isDirty: form.touchedFields.length > 0,
    nameError: form.touchedFields.includes("name") ? nameResult.error : undefined,
    onNameChange: (name: string) => update({ name }, "name"),
    onSelectedTypeChange: (selectedType: DateCalculationType) => update({ selectedType }, "selectedType"),
    onSelectedFormulaParameterIdsChange: (selectedFormulaParameterIds: string[]) =>
      update({ selectedFormulaParameterIds }, "selectedFormulaParameterIds"),
    onSelectedDateFieldChange: (selectedDateField: string) => update({ selectedDateField }, "selectedDateField"),
  };
};

export default useSetupDateCalculationMenu;
